//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useMemo, useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  Checkbox,
  TextField,
} from '@mui/material';
import {
} from '@mui/icons-material';

// === COMPONENTS ===
import Collapsible, { Header } from 'components/Collapsible';
import {Spacer} from 'components/Spacer';
import Styled from 'components/Styled/Styled';
import {emit, on, removeListener, sendMessageData} from 'api/prokey/prokey.websocket';
import {useFile} from '../FileContext/FileContext';
import {getHexAlpha} from 'components/styles.utils';

import useOnVisible from 'components/hooks/useOnVisible/useOnVisible';

//#endregion

const CategoryMemo = React.memo( Category );
const DocumentGroupMemo = React.memo( DocumentGroup );
const DocumentMemo = React.memo( Document );

//-----------------------------------------------------------------
export function DocumentCategoriesList( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const { file_id, is_loading } = useFile();

  const [ categories, setCategories ] = useState([]);
  const [ checked   , setChecked    ] = useState(false);

  const _tags = [
    // { label: 'My Files'    , group: 'Assigned User' , filter: (file) => true },
    // { label: 'Other Files' , group: 'Assigned User' , filter: (file) => true },
    // { label: 'Complete'    , group: 'Status'        , filter: (file) => true },
    // { label: 'In Progress' , group: 'Status'        , filter: (file) => true },
  ];

  //#endregion

  //#region [STYLES]

  const scrollbar_thin = {
    // scrollbarColor : `${theme.palette.primary.main} ${theme.palette.background.secondary[theme.palette.mode]}`,
    scrollbarWidth : 'thin'
  }
  
  const _bg_pd_blue     = {background  : theme.palette.primary.main}
  const _bg_secondary   = {background  : theme.palette.background.secondary[theme.palette.mode]}
  const _color_pd_blue  = {color       : theme.palette.primary.main}
  const _border_pd_blue = {borderColor : theme.palette.primary.main}
  const _border_bottom  = {borderBottom:'4px solid'}
  const _no_shrink      = {flexShrink:0}
  const _pointer        = {cursor: 'pointer'};
  const _size_30px      = {height:'30px', width:'30px'}
  const _flex           = {display:'flex'}
  const _center_items   = { justifyContent: 'center', alignItems: 'center'}

  const FILTER_TAGS = {
    id          : 'filter-tags',
    sx          : { width: '100%'},
    multiple    : true,
    options     : _tags,
    groupBy     : (option) => option.group,
    renderInput : (params) => { return <TextField {...params} variant='standard' label="Filter Tags" /> },
    onChange    : (event, value) => { props.onFilter( value.map( x => x.filter ) ) },
    name        : 'da_filter_tags_select',
  };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    if( is_loading ) return;

    const CATEGORIES = 'FILE_CATEGORIES';
    
    //-----------------------------------------------------------------
    function handleCategories( data ) {
      //-----------------------------------------------------------------
      if( !Array.isArray(data) ) {
        console.info('invalid category list', data );
      }
      if( data?.error ) {
        return;
      }
      setCategories( data );
    }

    on( CATEGORIES, handleCategories );

    sendMessageData( 'FILE_REQUEST_CATEGORIES', { file_id } )

    return () => {
      removeListener( CATEGORIES, handleCategories );
    }
  }, [file_id, is_loading])

  //#endregion

  //#region [EVENTS] 

  //-----------------------------------------------------------------
  function onChange_Select( event ) {
    //-----------------------------------------------------------------
    setChecked( !checked );
    sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, is_selected: event.target.checked, is_all: true } )
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const display_data = categories?.map( cat => <Styled key={cat.id} styles={[{height: 'fit-content'}]}><CategoryMemo id={cat.id} /></Styled> ) || null;

    return (
      <Styled styles={[]}>
        <Banner />
        <Styled styles={[{overflow: 'auto'},scrollbar_thin,{height: 'calc( 100% - 60px )'} ]}>
          {display_data}
        </Styled>
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function Banner() {
    //-----------------------------------------------------------------
    return (
      <Styled styles={[_flex,{height:'60px'},_bg_secondary, _center_items]} >
        <Styled styles={[_flex, _center_items]}>
          <Checkbox checked={checked} onChange={onChange_Select}/>
          Open All
        </Styled>
        <Styled styles={[ _flex, _center_items]}>
          <Styled styles={[_flex, _center_items]}>
            <Autocomplete {...FILTER_TAGS} />
          </Styled>
        </Styled>
        <Spacer fill />
      </Styled>
    )
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function Category( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ category, setCategory ] = useState(null);

  const _bg_pd_blue   = { background: theme.palette.primary.main };
  const _bg_primary   = { background: theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background: theme.palette.background.secondary[theme.palette.mode] };
  const _tc_pd_white  = { color: theme.palette.primary.contrastText }

  //#endregion

  //#region [STYLES]



  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const message_type = 'FILE_CATEGORY';
    
    //-----------------------------------------------------------------
    function handleCategory( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setCategory( data );
    }

    on( message_type, handleCategory );

    sendMessageData( 'FILE_REQUEST_CATEGORY', { file_id, category_id: id } )

    return () => {
      removeListener( message_type, handleCategory );
    }
  }, [])

  //#endregion

  //#region [EVENTS] 


  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( category == null ) return null;
    if( category.types === null ) return null;
    if( category.doc_count <= 0 ) return null;

    const child = (
      <Styled key='doc-groups'>
        <Styled styles={[{height:'1rem'}]}/>
        {displayGroups()}
        <Styled styles={[{height:'1rem'}]}/>
      </Styled>
    )
    return (
      <Collapsible
        key={id}
        styles={[]}
        header        = {{ children: <Header height={35} title={category.name} styles={[{fontWeight: 'bold'}]}/>, styles:[ _bg_pd_blue, _tc_pd_white ] }}
        header_closed = {{ children: <Header height={35} title={category.name} styles={[{fontWeight: 'bold'}]}/>, styles:[ _bg_pd_blue, _tc_pd_white ]  }}
        body          = {
          {
            children: child,
            styles:[ {height:'fit-content'} ]
          }
        }
        body_closed   = {{ children: null }}
      />
    );
  }

  //-----------------------------------------------------------------
  function displayGroups() {
    //-----------------------------------------------------------------
    if( category?.types == null ) return null;

    return category.types.order.map( x => {
      const type = category.types.items[x];
      return (
       <DocumentGroupMemo key={x} group={type} /> 
      )
    })
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function DocumentGroup( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]
  const { group } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ doc_group, setGroup   ] = useState({});
  const [ checked  , setChecked ] = useState(false);

  const _bg_primary   = { background: theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background: theme.palette.background.secondary[theme.palette.mode] };

  //#endregion

  //#region [STYLES]

  const _pd_blue = theme.palette.primary.main;
  const _secondary = theme.palette.background.secondary[theme.palette.mode];
  const no_margins    = { margin     : 0        };
  const center_items  = { alignItems : 'center' };
  const _size_v_2rem  = { height     : '2rem'   };
  const grid          = { display    : 'grid'   };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const message_type = 'FILE_DOCUMENT_GROUP'+'_'+group.numcode;
    
    //-----------------------------------------------------------------
    function handleDocumentUpdate( data ) {
      //-----------------------------------------------------------------
      setGroup( data )
    }

    on( message_type, handleDocumentUpdate );

    return () => {
      removeListener( message_type, handleDocumentUpdate );
    }
  }, [])

  //#endregion

  //#region [EVENTS] 

  //-----------------------------------------------------------------
  function onChange_Select( event ) {
    //-----------------------------------------------------------------
    // sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: group.documents, is_selected: event.target.checked, is_multi: true } )
  }

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if( event.ctrlKey && event.button === 0 ) {
      setChecked( !checked );
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: group.documents, is_selected: !checked,  is_multi: true } )
    } else if(event.button === 0) {
      setChecked( !checked );
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: group.documents, is_selected: !checked } )
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( group.documents.length <= 0 ) {
      return null;
    }
    return (
      <Collapsible
        key={group.numcode}
        styles={ [
          {height:'fit-content'},
        ]}
        header        = {{ children: <Header height={30} title={group.numcode + ' - ' + group.name} styles={[ {fontWeight: 'bold'}, _bg_secondary ]} />, styles:[_bg_secondary] }}
        header_closed = {{ children: <Header height={30} title={group.numcode + ' - ' + group.name} styles={[ {fontWeight: 'bold'} ]} /> }}
        body          = {{
          children: 
            <Styled styles={[{height:'fit-content'}]}>
              {displayBody()}
              <Styled key='spacer_2' styles={[{height:'1rem'},]}/>
            </Styled>,
          styles: [{height:'fit-content'}, {paddingLeft: '2rem', paddingRight: '1rem'} ]
        }}
        body_closed   = {{ children: null, styles: [{height: 0}] }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayBody() {
    //-----------------------------------------------------------------
    if( group.documents.length <= 0 ) {
      return <Styled styles={[{display: 'flex'}]}><Spacer width='2rem' />No documents found.</Styled>
    }

    const grid_columns = { gridTemplateColumns: `auto repeat(${group.headers.length}, 1fr)` };
    const docHeader = (
      <Styled styles={[_size_v_2rem, grid, grid_columns, center_items, no_margins,{cursor:'pointer'},_bg_secondary]}>
        <Styled styles={[{display:'flex'}]}>
          <Checkbox checked={checked} onMouseDown={onMouseDown} />
          <Spacer />
        </Styled>
        {group.headers.map( header => <Styled key={header} styles={[{fontWeight: 'bold'}]}>{header}</Styled> )}
      </Styled>
    )

    const documentList = group.documents.map( doc_id => {
      return <DocumentMemo key={doc_id} id={doc_id} />
    });

    return (
      <Styled styles={[{height:'fit-content'},{display: 'grid', gridTemplateRows: `repeat( ${group.documents.length+1}, 1fr )`}, {/*marginLeft: '2rem'*/}, {cursor: 'auto' },{overflow:'auto'}]}>
        {docHeader   }
        {documentList}
      </Styled>
    )
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function Document( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ document, setDocument ] = useState();

  const [checked, setChecked] = useState(false);
  const [focused, setFocused] = useState(false);

  const ref = useRef();
  const [is_visible] = useOnVisible(ref)

  //#endregion

  //#region [STYLES]

  const no_margins    = { margin     : 0        };
  const center_items  = { alignItems : 'center' };
  const _size_v_2rem  = { height     : '2rem'   };
  const grid          = { display    : 'grid'   };
  const _bg_pd_blue   = { background : theme.palette.primary.main                             };
  const _bg_primary   = { background : theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background : theme.palette.background.secondary[theme.palette.mode] };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const DOCUMENT_PREVIEW = 'FILE_DOCUMENT_PREVIEW';
    
    //-----------------------------------------------------------------
    function handleDocumentPreview( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setDocument( data )
    }
    //-----------------------------------------------------------------
    function handleDocument( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      if( data.is_selected == null ) return;
      setChecked( data.is_selected );
    }

    on( DOCUMENT_PREVIEW, handleDocumentPreview );
    on( 'FILE_DOCUMENT'      , handleDocument        );

    return () => {
      removeListener( DOCUMENT_PREVIEW, handleDocumentPreview );
      removeListener( 'FILE_DOCUMENT'      , handleDocument        );
    }
  }, [])

  useEffect( () => {
    if( !checked ) {
      if( focused ) {
        setFocused( false );
      }
    };

    function handleFocus(data) {
      if( data.id !== id && focused ) {
        setFocused( false );
      };
      if( data.id !== id ) return;
      setFocused( true );
      ref.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      ref.current.focus();
    }

    on( 'FILE_DOCUMENT_FOCUS', handleFocus );

    return () => {
      removeListener( 'FILE_DOCUMENT_FOCUS', handleFocus );
    }
  }, [checked, focused] );
  
  useEffect( () => {
    if( !is_visible ) return;
    sendMessageData( 'FILE_REQUEST_DOCUMENT_PREVIEW', { file_id, document_id: id } );
  }, [id,is_visible] )

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if( event.ctrlKey && event.button === 0 ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: !checked,  is_multi: true } )
    } else if( event.button === 0 && !checked ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: true } )
    } else if( event.button === 0 &&  checked ) {
      sendMessageData( 'FILE_DOCUMENT_FOCUS', { file_id, document_id: id } );
    }
  }

  //-----------------------------------------------------------------
  function onMouseDown_Checkbox(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
    event.stopPropagation();
    // Left mouse button
    if( event.ctrlKey && event.button === 0 ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: !checked,  is_multi: true } )
    } else if( event.button === 0 ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: !checked } )
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const grid_columns = { gridTemplateColumns: `auto repeat(${document?.display_fields?.length||0}, 1fr)` };
    const bg = focused ? { background: '#ffa500' + getHexAlpha(80) } : {}
    return (
      <Styled ref={ref} styles={[_size_v_2rem, grid, grid_columns, center_items, no_margins,{cursor:'pointer'},bg ]} onMouseDown={onMouseDown}>
        {
          document?.display_fields == null ? null
          : (
            <>
              <Styled styles={[{display:'flex'}]}>
                <Checkbox checked={checked} onMouseDown={onMouseDown_Checkbox} />
                <Spacer />
              </Styled>
              { document.display_fields.map( (data,index) => <Styled key={index} styles={[{fontWeight: 'bold'}]}>{data}</Styled> ) }
            </>
          )
        }
      </Styled>
    )
  }

  //#endregion

  return display();
}